/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Modal, Button } from "antd";
import { PiPrinterFill } from "react-icons/pi";
import logo from "../../../assets/images/logo_nsf.png";
import GoodsReceiptService from "../../../service/ERP/GoodsReceipt.service";
import dayjs from "dayjs";
import "./gr-qc.css";

const grService = GoodsReceiptService();

const PAGE_STYLE_ID = "gr-qc-print-style";

const RM_MIN_ROWS = 12;
const IG_MIN_ROWS = 10;
const CM_MIN_ROWS = 12;
const PK_MIN_ROWS = 10;

const fdate = (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "");
const chk = (v) => (Number(v) === 1 ? "✓" : Number(v) === 0 ? "✗" : "");
const yn = (v) => (Number(v) === 1 ? "✓" : Number(v) === 0 ? "-" : "");
/* ช่องแบบ "ไม่พบ = ปกติ" : 0 → / , 1 → X */
const chkNot = (v) => (Number(v) === 0 ? "✓" : Number(v) === 1 ? "✗" : "");
const dash = (v) => (v === 0 || v ? v : "");
/* รหัสสินค้าบนฟอร์ม — ใช้รหัสฝั่ง Express ถ้ามี ไม่งั้นใช้รหัสภายใน */
const codeDisplay = (d) => d?.express_code || d?.stcode || "";
/* ตัวเลขจำนวน — ใส่ , คั่นหลักพัน ทศนิยม 2 ตำแหน่ง */
const qnum = (v) =>
  v === 0 || v
    ? Number(v).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "";
/* Lot no. แบบรวม : "<express lot>/<lot ที่ระบบออกให้>" */
const lotDisplay = (d) => {
  if (!d) return "";
  const ex = (d.express_lot || "").trim();
  const lot = d.lot_no || "";
  return ex ? `${ex}/${lot}` : lot;
};
/* ฝั่ง IG ใช้ internal_lot เป็นเลข lot ของระบบ */
const internalLotDisplay = (d) => {
  if (!d) return "";
  const ex = (d.express_lot || "").trim();
  const lot = d.internal_lot || d.lot_no || "";
  return ex ? `${ex}/${lot}` : lot;
};

/* สารเคมี : รหัสขึ้นต้นด้วย IG7 → ใช้ฟอร์ม FM.QA.15 แทน FM.QA.12 */
const isChemical = (d) =>
  String(d?.stcode || "").toUpperCase().startsWith("IG7");

/* ภาชนะบรรจุ/บรรจุภัณฑ์ : รหัสขึ้นต้น PK หรือ PC → ใช้ฟอร์ม FM.WH.13 */
const isPacking = (d) => {
  const p = String(d?.stcode || "").toUpperCase().slice(0, 2);
  return p === "PK" || p === "PC";
};

/* ช่องวันที่บนหัวฟอร์ม : ยังไม่ได้กรอก → เว้นเส้นประไว้เขียนมือหน้างาน */
const BlankDate = () => <span className="qc-blank-date">&nbsp;</span>;
const dateOrBlank = (d) => (d ? fdate(d) : <BlankDate />);

/* หัวกระดาษฟอร์มคลัง (FM.WH.13) — 2 แถว 3 คอลัมน์ */
function WhFormHead({ title, formNo, receiveDate, pocode, supplier, invNo, invDate, reportSentDate }) {
  return (
    <div className="wh-head">
      <div className="wh-head-top">
        <img src={logo} alt="logo" className="wh-logo" />
        <div className="wh-head-title">
          <p className="wh-company">บริษัท ไนน์สตาร์ฟู้ด จำกัด</p>
          <p className="wh-title">{title}</p>
        </div>
        <div className="wh-head-formno">{formNo}</div>
      </div>
      <div className="wh-head-sub">
        <span>วันที่รับเข้า <u>&nbsp;{fdate(receiveDate)}&nbsp;</u></span>
        <span>PO <u>&nbsp;{pocode || ""}&nbsp;</u></span>
        <span>วันที่ส่งรายงาน <u>&nbsp;{dateOrBlank(reportSentDate)}&nbsp;</u></span>
        <span>บริษัทผู้จำหน่าย <u>&nbsp;{supplier || ""}&nbsp;</u></span>
        <span>เลขที่ INV/ใบส่งของ <u>&nbsp;{invNo || ""}&nbsp;</u></span>
        <span>วันที่ INV/ใบส่งของ <u>&nbsp;{invDate ? fdate(invDate) : ""}&nbsp;</u></span>
      </div>
    </div>
  );
}

/* หัวกระดาษบริษัท + ชื่อฟอร์ม */
function FormHead({ title, titleEn, formNo, inspectDate, dateLabel, supplier, reportSentDate }) {
  return (
    <div className="qc-head">
      <div className="qc-head-left">
        <img src={logo} alt="logo" className="qc-logo" />
      </div>
      <div className="qc-head-center">
        <p className="qc-title">{title}</p>
        <p className="qc-title-en">{titleEn}</p>
      </div>
      <div className="qc-head-right">{formNo}</div>
      <div className="qc-head-sub">
        <span>
          {dateLabel || "วันที่รับวัตถุดิบ"}&nbsp; <u>&nbsp;{fdate(inspectDate)}&nbsp;</u>
        </span>
        {supplier !== undefined && (
          <span>
            บริษัทผู้ส่ง/ผลิต (Supplier name)&nbsp; <u>&nbsp;{supplier || ""}&nbsp;</u>
          </span>
        )}
        <span>
          วันที่ส่งรายงาน&nbsp; <u>&nbsp;{dateOrBlank(reportSentDate)}&nbsp;</u>
        </span>
        <span className="qc-freq">ความถี่ในการปฏิบัติงาน : ทุกครั้งที่มีการรับเข้า</span>
      </div>
    </div>
  );
}

function SignRow({ notes }) {
  return (
    <div className="qc-foot">
      <div className="qc-notes">
        <div className="qc-notes-title">วิธีการตรวจรับ</div>
        {notes.map((n, i) => (
          <div className="qc-note-line" key={i}>{i + 1}. {n}</div>
        ))}
      </div>
      <div className="qc-signs">
        <div className="qc-sign"><div className="qc-sign-line" /><div>Inspector</div></div>
        <div className="qc-sign"><div className="qc-sign-line" /><div>QA Sup.</div></div>
        <div className="qc-sign"><div className="qc-sign-line" /><div>PD Sup.</div></div>
      </div>
    </div>
  );
}

/* กรอบพื้นเทา + ปุ่ม PRINT ของหน้าพิมพ์ปกติ
   โหมด embedded จะไม่ใส่กรอบ เพราะหน้าแม่ (Print รายวัน) ครอบให้อยู่แล้ว */
function PrintShell({ embedded, wrapRef, docRef, barRef, onPrint, children }) {
  if (embedded) return <>{children}</>;
  return (
    <div className="page-show" id="gr-qc" ref={wrapRef}>
      <div className="doc-wrapper" ref={docRef}>
        <div className="print-bar no-print" ref={barRef}>
          <Button
            className="bn-center bg-blue-400"
            icon={<PiPrinterFill style={{ fontSize: "1.1rem" }} />}
            onClick={() => onPrint()}
          >
            PRINT
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* embedded = ถูกเรียกจากหน้า Print รายวัน
   จะคืนเฉพาะตัวกระดาษ ไม่มีกรอบพื้นเทา/ปุ่ม PRINT (หน้าแม่จัดการให้แทน) */
/* sections : เลือกว่าจะพิมพ์ฟอร์มไหนบ้าง (ใช้ตอนรวมฟอร์มรายวัน)
   data     : ถ้าส่งมา จะใช้ข้อมูลชุดนี้แทนการยิง API ด้วย code */
export default function GrQcPrintPreview({
  code: codeProp,
  embedded = false,
  sections = ["RM", "IG", "CM", "PK"],
  data = null,
}) {
  const params = useParams();
  const code = codeProp || params?.code;
  const wrapRef = useRef(null);
  const docRef = useRef(null);
  const printRef = useRef(null);
  const barRef = useRef(null);
  const [gr, setGr] = useState({});
  const [qc, setQc] = useState({});
  const [rowsRM, setRowsRM] = useState([]);
  const [rowsIG, setRowsIG] = useState([]);
  const [rowsCM, setRowsCM] = useState([]);
  const [rowsPK, setRowsPK] = useState([]);

  /* สั่งพิมพ์ด้วย window.print() เหมือนหน้า print อื่นของระบบ
     (gr-qc.css คุมโหมดพิมพ์ด้วยเทคนิค visibility hidden/visible อยู่แล้ว)

     เดิมใช้ useReactToPrint ซึ่งพิมพ์ผ่าน iframe จึงไม่เข้ากับ CSS ชุดนี้
     และกดปุ่ม PRINT แล้วไม่มีอะไรเกิดขึ้น */
  const handlePrint = () => window.print();

  /* @page scope ตาม element ไม่ได้ — ฟอร์มนี้ต้องใช้ A4 แนวนอน
     จึงฉีดตอนเข้าหน้า (อยู่ท้าย cascade จึงชนะ @page ของหน้า print อื่น)
     แล้วถอดออกตอนออกจากหน้า */
  useEffect(() => {
    let style = document.getElementById(PAGE_STYLE_ID);
    if (!style) {
      style = document.createElement("style");
      style.id = PAGE_STYLE_ID;
      style.media = "print";
      style.innerHTML = "@page { size: A4 landscape; margin: 0; }";
      document.head.appendChild(style);
    }
    return () => {
      const s = document.getElementById(PAGE_STYLE_ID);
      if (s) s.remove();
    };
  }, []);

  useEffect(() => {
    const fit = () => {
      const wrap = wrapRef.current, doc = docRef.current, layout = printRef.current;
      if (!wrap || !doc || !layout) return;
      const availW = wrap.clientWidth - 32;
      const baseW = layout.offsetWidth;             // ความกว้างจริงของกระดาษ (A4 นอน)
      // ย่อเฉพาะเมื่อจอแคบกว่ากระดาษ ด้วย zoom (ระดับ layout จริง → scroll ถูกต้อง)
      const scale = Math.min(availW / baseW, 1);
      doc.style.zoom = scale;
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [rowsRM, rowsIG, rowsCM, rowsPK]);

  /* data เป็น object ใหม่ทุกครั้งที่ parent render — ใช้ key ที่เสถียรกัน effect วนซ้ำ */
  const dataKey = data ? JSON.stringify(data) : null;

  useEffect(() => {
    /* โหมดรับข้อมูลมาจากหน้าแม่ (พิมพ์รายวันแบบรวมฟอร์ม) */
    if (data) {
      setGr(data.gr || {});
      setQc(data.qc || {});
      setRowsRM(data.rowsRM || []);
      setRowsIG(data.rowsIG || []);
      setRowsCM(data.rowsCM || []);
      setRowsPK(data.rowsPK || []);
      return;
    }
    grService.qcGet(code).then((res) => {
      const { gr: g, qc: q, detail } = res.data?.data || {};
      setGr(g || {});
      setQc(q || {});
      const all = detail || [];
      setRowsRM(all.filter((d) => d.report_type !== "IG"));
      /* IG7 = สารเคมี → FM.QA.15 , PK/PC = ภาชนะบรรจุ-บรรจุภัณฑ์ → FM.WH.13
         (ในฐานข้อมูลยังเป็น IG เหมือนเดิม แยกด้วยรหัสสินค้าตอนแสดงผล) */
      setRowsIG(
        all.filter((d) => d.report_type === "IG" && !isChemical(d) && !isPacking(d))
      );
      setRowsCM(all.filter((d) => d.report_type === "IG" && isChemical(d)));
      setRowsPK(all.filter((d) => d.report_type === "IG" && isPacking(d)));
    }).catch((err) => {
      Modal.error({
        title: "Error",
        content: err.response?.data?.message || "Request failed.",
        onOk: () => { if (!embedded) window.close(); },
      });
    });
  }, [code, dataKey]);

  const inspectDate = qc?.inspect_date || gr?.grdate;
  /* วันที่ส่งรายงานผลตรวจ — ไม่กรอกก็พิมพ์ได้ จะเว้นเส้นประไว้เขียนมือ */
  const reportSentDate = qc?.report_sent_date || null;
  /* ชื่อผู้จำหน่าย : ใช้จากใบรับสินค้าเป็นหลัก ถ้าไม่มีก็ใช้ชื่อที่บันทึกไว้ในผลตรวจ */
  const supplierName =
    gr?.supname ||
    [...rowsRM, ...rowsIG, ...rowsCM, ...rowsPK].find((d) => d?.supplier)?.supplier ||
    "";
  const padRM = [...rowsRM, ...Array(Math.max(0, RM_MIN_ROWS - rowsRM.length)).fill(null)];
  const padIG = [...rowsIG, ...Array(Math.max(0, IG_MIN_ROWS - rowsIG.length)).fill(null)];
  const padCM = [...rowsCM, ...Array(Math.max(0, CM_MIN_ROWS - rowsCM.length)).fill(null)];
  const padPK = [...rowsPK, ...Array(Math.max(0, PK_MIN_ROWS - rowsPK.length)).fill(null)];

  return (
    <PrintShell
      embedded={embedded}
      wrapRef={wrapRef}
      docRef={docRef}
      barRef={barRef}
      onPrint={handlePrint}
    >
        <div ref={printRef}>

          {/* ============ RM : FM.QA.11/RI ============ */}
          {sections.includes("RM") && rowsRM.length > 0 && (
            <div className="print-page">
              <FormHead
                title="รายงานตรวจสอบวัตถุดิบรับเข้า"
                titleEn="Report of Raw Material Received ( RM )"
                formNo="FM.QA.11/RI"
                inspectDate={inspectDate}
                reportSentDate={reportSentDate}
              />
              <table className="qc-table">
                <thead>
                  <tr>
                    <th rowSpan={2} className="w-time">Time<br /><em>เวลาที่รับเข้า</em></th>
                    <th rowSpan={2} className="w-code">Code<br /><em>รหัสสินค้า</em></th>
                    <th rowSpan={2}>Raw material<br /><em>ชื่อวัตถุดิบ พืช/ผัก</em></th>
                    <th rowSpan={2} className="w-sm">Type<br /><em>ลักษณะ</em></th>
                    <th rowSpan={2}>Supplier<br /><em>ผู้จำหน่าย</em></th>
                    <th rowSpan={2} className="w-xs">Transfer<br /><em>ขนส่ง</em></th>
                    <th rowSpan={2} className="w-xs">Packaging<br /><em>บรรจุ</em></th>
                    <th colSpan={4}>ตำหนิ (จำนวน)</th>
                    <th rowSpan={2} className="w-sm">Total Qty<br /><em>จำนวน</em></th>
                    <th rowSpan={2} className="w-sm">Weight<br /><em>(Kg)</em></th>
                    <th rowSpan={2} className="w-lot">Lot no.</th>
                    <th colSpan={2}>Conclude</th>
                    <th rowSpan={2} className="w-md">การแก้ไข</th>
                  </tr>
                  <tr>
                    <th className="w-xs">เน่าเสีย</th>
                    <th className="w-xs">แมลง</th>
                    <th className="w-xs">แปลกปลอม</th>
                    <th className="w-xs">อื่นๆ</th>
                    <th className="w-xs">Accept</th>
                    <th className="w-xs">Reject</th>
                  </tr>
                </thead>
                <tbody>
                  {padRM.map((d, i) => (
                    <tr key={i}>
                      <td className="c">{d?.inspect_time}</td>
                      <td className="c">{codeDisplay(d)}</td>
                      <td className="l">{d?.stname}</td>
                      <td className="c">{d?.mat_type}</td>
                      <td className="l">{d ? d.supplier || supplierName : ""}</td>
                      <td className="c">{d ? chk(d.transfer_ok) : ""}</td>
                      <td className="c">{d ? chk(d.packaging_ok) : ""}</td>
                      <td className="c">{d ? dash(d.def_spoiled) : ""}</td>
                      <td className="c">{d ? dash(d.def_insect) : ""}</td>
                      <td className="c">{d ? dash(d.def_foreign) : ""}</td>
                      <td className="c">{d ? dash(d.def_other) : ""}</td>
                      <td className="r">{d ? `${qnum(d.total_qty)} ${d.pack_unit || ""}` : ""}</td>
                      <td className="r">{d ? qnum(d.total_weight) : ""}</td>
                      <td className="c">{lotDisplay(d)}</td>
                      <td className="c">{d && d.conclude === "accept" ? "✓" : ""}</td>
                      <td className="c">{d && d.conclude === "reject" ? "✓" : ""}</td>
                      <td className="l">{d?.correction}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <SignRow notes={[
                "สุ่มตัวอย่างเพื่อนำมาตรวจสอบตามที่ได้ระบุไว้ในแผนการสุ่มตัวอย่าง",
                "วิธีการตรวจสอบ ให้ปฏิบัติตามเอกสาร วิธีการตรวจรับวัตถุดิบ สารเคมี-ส่วนผสมรับเข้า",
                "หากพบปัญหาในการตรวจรับ ให้แจ้งหัวหน้าฝ่ายผลิต และหัวหน้าแผนกควบคุมคุณภาพให้รับทราบ",
                "จดบันทึกปัญหาและวิธีการแก้ปัญหาลงในช่องหมายเหตุ",
              ]} />
            </div>
          )}

          {/* ============ IG : FM.QA.12/RI ============ */}
          {sections.includes("IG") && rowsIG.length > 0 && (
            <div className="print-page">
              <FormHead
                title="รายงานตรวจสารเคมี - ส่วนผสมรับเข้า"
                titleEn="Report of Ingredients Received ( IG )"
                formNo="FM.QA.12/RI"
                inspectDate={inspectDate}
                reportSentDate={reportSentDate}
              />
              <table className="qc-table">
                <thead>
                  <tr>
                    <th className="w-time">Time<br /><em>เวลาที่รับเข้า</em></th>
                    <th className="w-code">Code<br /><em>รหัสสินค้า</em></th>
                    <th>Ingredients name<br /><em>ชื่อสารเคมี-ส่วนผสม</em></th>
                    <th>Supplier<br /><em>ผู้จำหน่าย</em></th>
                    <th className="w-xs">Transfer<br /><em>ขนส่ง</em></th>
                    <th className="w-xs">Packaging<br /><em>บรรจุ</em></th>
                    <th className="w-xs">สิ่งแปลก<br />ปลอม</th>
                    <th className="w-xs">Pest/<br />Insect</th>
                    <th className="w-sm">Total Qty<br /><em>จำนวน</em></th>
                    <th className="w-sm">Weight<br /><em>(Kg)</em></th>
                    <th className="w-md">Production<br />date</th>
                    <th className="w-md">Expiry<br />date</th>
                    <th className="w-xs">COA</th>
                    <th className="w-md">Supplier<br />COA/Lot no.</th>
                    <th className="w-lot">Internal<br />Lot No.</th>
                    <th className="w-sm">Conclude<br /><em>A / R</em></th>
                    <th className="w-md">การแก้ไข</th>
                  </tr>
                </thead>
                <tbody>
                  {padIG.map((d, i) => (
                    <tr key={i}>
                      <td className="c">{d?.inspect_time}</td>
                      <td className="c">{codeDisplay(d)}</td>
                      <td className="l">{d?.stname}</td>
                      <td className="l">{d ? d.supplier || supplierName : ""}</td>
                      <td className="c">{d ? chk(d.transfer_ok) : ""}</td>
                      <td className="c">{d ? chk(d.packaging_ok) : ""}</td>
                      <td className="c">{d ? yn(d.foreign_matter) : ""}</td>
                      <td className="c">{d ? yn(d.pest_insect) : ""}</td>
                      <td className="r">{d ? `${qnum(d.total_qty)} ${d.pack_unit || ""}` : ""}</td>
                      <td className="r">{d ? qnum(d.total_weight) : ""}</td>
                      <td className="c">{fdate(d?.production_date)}</td>
                      <td className="c">{fdate(d?.expiry_date)}</td>
                      <td className="c">{d ? yn(d.coa_status) : ""}</td>
                      <td className="c">{d?.supplier_lot}</td>
                      <td className="c">{internalLotDisplay(d)}</td>
                      <td className="c">{d ? (d.conclude === "accept" ? "A" : d.conclude === "reject" ? "R" : "") : ""}</td>
                      <td className="l">{d?.correction}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <SignRow notes={[
                "วิธีการตรวจสอบ ให้ปฏิบัติตามเอกสาร วิธีการตรวจรับวัตถุดิบ สารเคมี-ส่วนผสมรับเข้า",
                "หากพบปัญหาในการตรวจรับ ให้แจ้งหัวหน้าฝ่ายผลิต และหัวหน้าแผนกควบคุมคุณภาพให้รับทราบ",
                "จดบันทึกปัญหาและวิธีการแก้ปัญหาลงในช่องหมายเหตุ",
              ]} />
            </div>
          )}

          {/* ============ CM : สารเคมี IG7* (FM.QA.15/R0) ============ */}
          {sections.includes("CM") && rowsCM.length > 0 && (
            <div className="print-page">
              <FormHead
                title="รายงานตรวจสอบสารเคมีรับเข้า"
                titleEn="Report of Chemical Received ( CM )"
                formNo="FM.QA.15 / R0"
                inspectDate={inspectDate}
                dateLabel="วันที่รับเข้า"
                supplier={supplierName}
                reportSentDate={reportSentDate}
              />
              <table className="qc-table">
                <thead>
                  <tr>
                    <th className="w-xs">ลำดับที่</th>
                    <th>Name<br /><em>ชื่อสารเคมี</em></th>
                    <th className="w-sm">Transfer<br /><em>สภาพขนส่ง</em><br /><em>สะอาด/ไม่สะอาด</em></th>
                    <th className="w-sm">Packaging<br /><em>สภาพการบรรจุ</em></th>
                    <th className="w-md">Total Quantity<br /><em>จำนวนทั้งหมด</em><br /><em>(กล่อง/ถัง/ถุง/ปี๊บ)</em></th>
                    <th className="w-sm">Total weight<br /><em>น้ำหนักรวม (Kg)</em></th>
                    <th className="w-md">Production<br />date<br /><em>วันที่ผลิต</em></th>
                    <th className="w-md">Expiry<br />date<br /><em>วันหมดอายุ</em></th>
                    <th className="w-sm">COA<br /><em>ผลตรวจสารเคมี</em></th>
                    <th className="w-lot">Lot No.<br /><em>หมายเลขล็อต</em></th>
                    <th className="w-md">Conclude<br /><em>สรุปผลการตรวจรับ</em></th>
                    <th className="w-md">หมายเหตุ /<br />การแก้ไข</th>
                  </tr>
                </thead>
                <tbody>
                  {padCM.map((d, i) => (
                    <tr key={i}>
                      <td className="c">{d ? i + 1 : ""}</td>
                      <td className="l">{d?.stname}</td>
                      <td className="c">{d ? chk(d.transfer_ok) : ""}</td>
                      <td className="c">{d ? chk(d.packaging_ok) : ""}</td>
                      <td className="r">{d ? `${qnum(d.total_qty)} ${d.pack_unit || ""}` : ""}</td>
                      <td className="r">{d ? qnum(d.total_weight) : ""}</td>
                      <td className="c">{fdate(d?.production_date)}</td>
                      <td className="c">{fdate(d?.expiry_date)}</td>
                      <td className="c">{d ? yn(d.coa_status) : ""}</td>
                      <td className="c">{internalLotDisplay(d)}</td>
                      <td className="c">{d ? (d.conclude === "accept" ? "✓ Accept" : d.conclude === "reject" ? "✗ Reject" : "") : ""}</td>
                      <td className="l">{d?.correction}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <SignRow notes={[
                "วิธีการตรวจสอบ ให้ปฏิบัติตามเอกสาร วิธีการตรวจรับวัตถุดิบ สารเคมี-ส่วนผสมรับเข้า",
                "หากพบปัญหาในการตรวจรับ ให้แจ้งหัวหน้าฝ่ายผลิต และหัวหน้าแผนกควบคุมคุณภาพให้รับทราบ",
                "จดบันทึกปัญหาและวิธีการแก้ปัญหาลงในช่องหมายเหตุ",
              ]} />
            </div>
          )}

          {/* ====== PK : ภาชนะบรรจุ/บรรจุภัณฑ์ PK*-PC* (FM.WH.13/R1) ====== */}
          {sections.includes("PK") && rowsPK.length > 0 && (
            <div className="print-page">
              <WhFormHead
                title="บันทึกการรับเข้าภาชนะบรรจุ/บรรจุภัณฑ์"
                formNo="FM.WH.13/R1"
                receiveDate={inspectDate}
                pocode={gr.pocode}
                supplier={supplierName}
                invNo={gr.inv_no}
                invDate={gr.inv_date}
                reportSentDate={reportSentDate}
              />
              <table className="qc-table wh-table">
                <thead>
                  <tr>
                    <th className="w-xs">ลำดับ</th>
                    <th className="w-code">รหัสสินค้า</th>
                    <th>ชื่อรายการ</th>
                    <th className="w-sm">Unit<br /><em>กล่อง,พาเลท</em></th>
                    <th className="w-sm">Total<br /><em>กล่อง,พาเลท,แพค</em></th>
                    <th className="w-md">Total Quantity<br /><em>จำนวนทั้งหมด</em></th>
                    <th className="w-vt"><span className="vt">สภาพภาชนะขนส่ง<br />ปกติ / ผิดปกติ</span><div className="vt-mark">/ &nbsp; X</div></th>
                    <th className="w-vt"><span className="vt">ความสะอาด<br />สะอาด / ไม่สะอาด</span><div className="vt-mark">/ &nbsp; X</div></th>
                    <th className="w-vt"><span className="vt">การฉีกขาดของภาชนะ<br />/ บรรจุภัณฑ์</span><div className="vt-mark">/ &nbsp; X</div></th>
                    <th className="w-vt"><span className="vt">กลิ่นผิดปกติ</span><div className="vt-mark">/ &nbsp; X</div></th>
                    <th className="w-vt"><span className="vt">ร่องรอยแมลง</span><div className="vt-mark">/ &nbsp; X</div></th>
                    <th className="w-md">สารก่อภูมิแพ้</th>
                    <th className="w-lot">Lot No.<br /><em>หมายเลขล็อต</em></th>
                    <th className="w-md">หมายเหตุ</th>
                  </tr>
                </thead>
                <tbody>
                  {padPK.map((d, i) => (
                    <tr key={i}>
                      <td className="c">{d ? i + 1 : ""}</td>
                      <td className="c">{codeDisplay(d)}</td>
                      <td className="l">{d?.stname}</td>
                      <td className="c">{d?.pack_unit}</td>
                      {/* ช่อง Total ปล่อยว่างให้เขียนจำนวนกล่อง/พาเลทเอง
                          จำนวนที่รับจริงไปแสดงในช่อง จำนวนทั้งหมด
                          (ข้อมูลเก่าบางใบเก็บจำนวนไว้ที่ total_weight จึงเผื่อไว้ด้วย) */}
                      <td className="r"></td>
                      <td className="r">
                        {d ? qnum(d.total_qty ?? d.total_weight) : ""}
                      </td>
                      <td className="c">{d ? chk(d.transfer_ok) : ""}</td>
                      <td className="c">{d ? chk(d.packaging_ok) : ""}</td>
                      <td className="c">{d ? chkNot(d.def_foreign) : ""}</td>
                      <td className="c">{d ? chkNot(d.def_other) : ""}</td>
                      <td className="c">{d ? chkNot(d.pest_insect) : ""}</td>
                      <td className="l">{d?.mat_type}</td>
                      <td className="c">{internalLotDisplay(d)}</td>
                      <td className="l">{d?.correction}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="wh-foot">
                <div className="wh-sign"><span>ผู้บันทึก</span><u>&nbsp;&nbsp;</u></div>
                <div className="wh-sign"><span>ผู้ตรวจสอบ</span><u>&nbsp;&nbsp;</u></div>
              </div>
            </div>
          )}

        </div>
    </PrintShell>
  );
}
